"use client";

import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { useLanguage } from "@/reusable_sections/Header";
export default function ClientSuccessStories() {
  const {
    lang
  } = useLanguage();
  const testimonials = [{
    author: "Marc D.",
    quoteEn: "STEGY GROUP LLC provided the clarity I needed to navigate complex international compliance. Their expertise in Haitian law is unmatched.",
    quoteFr: "STEGY GROUP LLC m'a apporté la clarté nécessaire pour naviguer dans la conformité internationale complexe. Leur expertise en droit haïtien est inégalée.",
    quoteHt: "STEGY GROUP LLC te ban mwen klète mwen te bezwen pou m navige nan konfòmite entènasyonal konplèks. Eksperyans yo nan lwa ayisyen pa gen parèy.",
    positionEn: "Corporate Executive",
    positionFr: "Cadre d'Entreprise",
    positionHt: "Kad Antrepriz",
    projectEn: "International Compliance",
    projectFr: "Conformité Internationale",
    projectHt: "Konfòmite Entènasyonal"
  }, {
    author: "Elena R.",
    quoteEn: "The guidance on my immigration pathway was precise and professional. I felt supported at every stage of the process.",
    quoteFr: "L'accompagnement pour ma démarche d'immigration a été précis et professionnel. Je me suis sentie soutenue à chaque étape du processus.",
    quoteHt: "Konsèy sou chemen imigrasyon mwen an te egzak e pwofesyonèl. Mwen te santi m soutni nan chak etap pwosesis la.",
    positionEn: "Private Client",
    positionFr: "Cliente Particulière",
    positionHt: "Kliyan Prive",
    projectEn: "Immigration Strategy",
    projectFr: "Stratégie d'Immigration",
    projectHt: "Estrateji Imigrasyon"
  }];
  const [activeIndex, setActiveIndex] = useState(0);
  const [, setIsTransitioning] = useState(false);
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  useEffect(() => {
    const interval = setInterval(() => {
      handleChange((activeIndex + 1) % testimonials.length);
    }, 8000);
    return () => clearInterval(interval);
  }, [activeIndex, testimonials.length]);
  const handleChange = (newIndex: number) => {
    if (newIndex === activeIndex) return;
    setIsTransitioning(true);
    setTimeout(() => {
      setActiveIndex(newIndex);
      setIsTransitioning(false);
    }, 600);
  };
  const handlePrev = () => {
    handleChange((activeIndex - 1 + testimonials.length) % testimonials.length);
  };
  const handleNext = () => {
    handleChange((activeIndex + 1) % testimonials.length);
  };
  const current = testimonials[activeIndex];
  const currentQuote = lang === "fr" ? current.quoteFr : lang === "ht" ? current.quoteHt : current.quoteEn;
  const currentPosition = lang === "fr" ? current.positionFr : lang === "ht" ? current.positionHt : current.positionEn;
  const currentProject = lang === "fr" ? current.projectFr : lang === "ht" ? current.projectHt : current.projectEn;
  const sectionHeading = lang === "fr" ? "Témoignages — 04" : lang === "ht" ? "Temwayaj — 04" : "Testimonials — 04";
  return <section data-section-id="2844" id="client-success-stories" className="bg-background text-foreground relative overflow-hidden py-16 md:py-20">
      {}
      <div className="absolute inset-y-0 left-0 hidden items-center px-6 md:flex lg:px-12">
        <Button variant="ghost" size="icon" onClick={handlePrev} aria-label="Previous testimonial" className="text-chart-1 hover:bg-transparent hover:text-chart-2 transition-colors duration-500">
          <ChevronLeft className="size-6" strokeWidth={1} />
        </Button>
      </div>
      <div className="absolute inset-y-0 right-0 hidden items-center px-6 md:flex lg:px-12">
        <Button variant="ghost" size="icon" onClick={handleNext} aria-label="Next testimonial" className="text-chart-1 hover:bg-transparent hover:text-chart-2 transition-colors duration-500">
          <ChevronRight className="size-6" strokeWidth={1} />
        </Button>
      </div>

      <div ref={ref} className={`mx-auto max-w-6xl px-6 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
        {}
        <div className="mb-10 flex items-center justify-center gap-4 md:mb-12">
          <div className="h-px w-12 bg-chart-1/40 md:w-24" />
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-chart-1 md:text-xs">
            {sectionHeading}
          </span>
          <div className="h-px w-12 bg-chart-1/40 md:w-24" />
        </div>

        {}
        <div className="relative md:min-h-[240px]">
          <div className="transition-opacity duration-700 ease-in-out opacity-100">
            <div className="relative mx-auto max-w-3xl">
              {}
              <div aria-hidden="true" className="font-serif absolute -top-8 -left-2 select-none leading-none text-chart-1/20 text-6xl md:-top-10 md:-left-4 md:text-8xl">
                &ldquo;
              </div>

              {}
              <blockquote className="relative z-10 pt-4 md:pt-6">
                <p className="font-serif italic font-light leading-[1.6] text-chart-1 text-xl md:text-2xl">
                  {currentQuote}
                </p>
              </blockquote>

              {}
              <div className="mt-8 h-px w-[60px] bg-chart-2 md:mt-10" />

              {}
              <div className="mt-5">
                <div className="font-default text-sm font-medium uppercase tracking-[0.2em] text-chart-1">
                  {current.author}
                </div>
                <div className="mt-1 font-mono text-xs uppercase tracking-[0.2em] text-secondary">
                  {currentPosition} — {currentProject}
                </div>
              </div>
            </div>
          </div>
        </div>

        {}
        <div className="mt-10 flex items-center justify-center gap-3">
          {testimonials.map((_, i) => <Button key={i} data-index={i} onClick={() => handleChange(i)} variant="ghost" size="sm" aria-label={`Go to testimonial ${i + 1}`} className={`h-[2px] px-0 transition-all duration-500 ease-out ${i === activeIndex ? "w-12 bg-chart-2" : "w-6 bg-chart-1/30 hover:bg-chart-1/50"}`} />)}
        </div>

        {}
        <div className="mt-6 flex items-center justify-center gap-6 md:hidden">
          <Button variant="ghost" size="icon" onClick={handlePrev} aria-label="Previous testimonial" className="text-chart-1 hover:bg-transparent">
            <ChevronLeft className="size-5" strokeWidth={1} />
          </Button>
          <Button variant="ghost" size="icon" onClick={handleNext} aria-label="Next testimonial" className="text-chart-1 hover:bg-transparent">
            <ChevronRight className="size-5" strokeWidth={1} />
          </Button>
        </div>
      </div>
    </section>;
}